import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, ViewStyle } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import WalletBetrBucksIcon from '@/assets/icons/wallet-betr-bucks';
import WalletCashIcon from '@/assets/icons/wallet-cash';
import WalletGoldIcon from '@/assets/icons/wallet-gold';
import { Button } from '@/components/ButtonComponent';
import { InnerBorder } from '@/components/InnerBorder';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { TouchableBox } from '@/components/lib/components/TouchableBox';
import { ds } from '@/components/lib/design.system';
import { AppColors, getThemeColorHexValue } from '@/components/lib/theme';
import { BONUS_CURRENCY, BonusCurrency } from '@/feature/account-details/components/wallet-card/utils';
import { Modal } from '@/feature/alerts/components/Modal';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { SocialCurrency } from '@/hooks/use-social-currency';
import { RootStackParamList } from '@/navigation/types';
import { withOpacity } from '@/styles/styles';
import { Currency as PickemCurrency } from '@/types/api.generated';
import { BottomSheetModal } from '@gorhom/bottom-sheet';

const FALLBACK_TERMS_URL = 'https://help.betr.app/en/collections/9579264-terms-and-conditions';

type CurrencyConfig = {
    color: AppColors;
    icon: React.ComponentType<{ size: number }>;
    labelKey: 'sweeps:currency_cash' | 'sweeps:gold_coins' | 'wallet:betr_bucks' | 'common:bonus';
};

type CurrencyTranslationKey =
    | 'cash_title'
    | 'cash_text_one'
    | 'cash_text_two'
    | 'cash_text_three'
    | 'cash_play_title'
    | 'cash_play_one'
    | 'coins_title'
    | 'coins_text_one'
    | 'coins_text_two'
    | 'coins_text_three'
    | 'coins_play_title'
    | 'coins_play_one'
    | 'coins_play_two'
    | 'fre_title'
    | 'fre_text_one'
    | 'fre_text_two'
    | 'fre_text_three'
    | 'fre_play_title'
    | 'fre_play_one'
    | 'fre_play_two'
    | 'bonus_title'
    | 'bonus_text_one'
    | 'bonus_text_two'
    | 'bonus_text_three'
    | 'bonus_play_title'
    | 'bonus_play_one'
    | 'bonus_play_two'
    | 'bonus_play_three';

type TabConfig = {
    currency: SocialCurrency | BonusCurrency;
    iconTransform: ViewStyle['transform'];
    iconSize: number;
    iconPosition: 'top-left' | 'bottom-right';
};

const CURRENCY_CONFIG: Record<string, CurrencyConfig> = {
    [SocialCurrency.CASH]: {
        color: ds.colorToken.background.success.strong,
        icon: WalletCashIcon,
        labelKey: 'sweeps:currency_cash',
    },
    [SocialCurrency.COINS]: {
        color: ds.colorToken.background.accent.amber.strong,
        icon: WalletGoldIcon,
        labelKey: 'sweeps:gold_coins',
    },
    [BONUS_CURRENCY]: {
        color: ds.colorToken.background.brand.strong,
        icon: WalletBetrBucksIcon,
        labelKey: 'common:bonus',
    },
    [PickemCurrency.Fre]: {
        color: ds.colorToken.background.brand.strong,
        icon: WalletBetrBucksIcon,
        labelKey: 'wallet:betr_bucks',
    },
};

const TAB_CONFIGS: TabConfig[] = [
    {
        currency: SocialCurrency.CASH,
        iconTransform: [{ rotate: '8deg' }],
        iconSize: 74,
        iconPosition: 'top-left',
    },
    {
        currency: SocialCurrency.COINS,
        iconTransform: [{ rotateY: '24deg' }, { rotateX: '-15deg' }],
        iconSize: 56,
        iconPosition: 'bottom-right',
    },
];

const getCurrencyConfig = (currency: SocialCurrency | PickemCurrency | BonusCurrency): CurrencyConfig => {
    return CURRENCY_CONFIG[currency] || CURRENCY_CONFIG[SocialCurrency.CASH];
};

const getCurrencyPrefix = (currency: SocialCurrency | PickemCurrency | BonusCurrency): string => {
    const prefixMap: Record<string, string> = {
        [SocialCurrency.CASH]: 'cash',
        [SocialCurrency.COINS]: 'coins',
        [BONUS_CURRENCY]: 'bonus',
        [PickemCurrency.Fre]: 'fre',
    };
    return prefixMap[currency] || 'cash';
};

const shouldShowTermsButton = (isTouchpoint: boolean, isCash: boolean) => {
    return !isTouchpoint || (isTouchpoint && isCash);
};

const styles = StyleSheet.create({
    iconTopLeft: {
        position: 'absolute',
        top: -31,
        left: -32,
    },
    iconBottomRight: {
        position: 'absolute',
        bottom: -16,
        right: -10,
    },
    tabContainer: {
        overflow: 'hidden',
    },
});

export interface ShowCurrencyOverviewModalOptions {
    isTouchpoint?: boolean;
    currency?: SocialCurrency | PickemCurrency | BonusCurrency;
}

export type CurrencyOverviewModalRef = {
    show: (options?: ShowCurrencyOverviewModalOptions) => void;
};

const BulletPoint = ({ text, color }: { text: string; color: AppColors }) => (
    <Row gap="s8" mb="s8" alignItems="flex-start">
        <Box mt="s10">
            <Box width={6} height={6} borderRadius="r6" backgroundColor={color} />
        </Box>
        <Box flex={1}>
            <Text variant="bodyMedium" color={ds.colorToken.text.secondary}>
                {text}
            </Text>
        </Box>
    </Row>
);

const CurrencyTab = ({
    isActive,
    icon,
    label,
    onPress,
    color,
    iconPosition,
}: {
    isActive: boolean;
    icon: React.JSX.Element;
    label: string;
    onPress: () => void;
    color: AppColors;
    iconPosition: 'top-left' | 'bottom-right';
}) => {
    const bgColor = isActive ? withOpacity(getThemeColorHexValue(color), 0.15) : 'transparent';

    return (
        <TouchableBox
            flex={1}
            style={[styles.tabContainer, { backgroundColor: bgColor }]}
            borderRadius="r96"
            px="s16"
            py="s12"
            onPress={onPress}
        >
            {isActive ? (
                <>
                    <InnerBorder borderRadius="r96" borderColor={ds.colorValue.border.strong} />
                    <Box style={iconPosition === 'top-left' ? styles.iconTopLeft : styles.iconBottomRight}>{icon}</Box>
                </>
            ) : null}
            <Row gap="s8" alignItems="center" justifyContent="center">
                <Text variant="titleMedium" color={isActive ? ds.colorToken.text.primary : 'neutral300'}>
                    {label}
                </Text>
            </Row>
        </TouchableBox>
    );
};

export const CurrencyOverviewModal = forwardRef<CurrencyOverviewModalRef, {}>((_props, ref) => {
    const modalRef = useRef<BottomSheetModal>(null);
    const [selectedTab, setSelectedTab] = useState<SocialCurrency | PickemCurrency | BonusCurrency>(
        SocialCurrency.CASH
    );
    // isTouchpoint flag is used to determine whether or not to hide the title and tabs, and display currency icon
    const [isTouchpoint, setIsTouchpoint] = useState(false);
    const { t } = useTranslation(['currency_overview_modal', 'sweeps', 'common', 'wallet']);
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    useImperativeHandle(ref, () => ({
        show: options => {
            const isTouch = options?.isTouchpoint ?? false;
            const defaultCurrency = isTouch ? PickemCurrency.Fre : SocialCurrency.CASH;
            setIsTouchpoint(isTouch);
            setSelectedTab(options?.currency ?? defaultCurrency);
            modalRef.current?.present();
        },
    }));

    const currencyConfig = getCurrencyConfig(selectedTab);
    const currencyPrefix = getCurrencyPrefix(selectedTab);
    const isCash = selectedTab === SocialCurrency.CASH;

    const content = useMemo(
        () => ({
            color: currencyConfig.color,
            title: t(`${currencyPrefix}_title` as CurrencyTranslationKey),
            getItems: [
                t(`${currencyPrefix}_text_one` as CurrencyTranslationKey),
                t(`${currencyPrefix}_text_two` as CurrencyTranslationKey),
                t(`${currencyPrefix}_text_three` as CurrencyTranslationKey),
            ],
            playTitle: t(`${currencyPrefix}_play_title` as CurrencyTranslationKey),
            playItems: [
                `${currencyPrefix}_play_one`,
                `${currencyPrefix}_play_two`,
                `${currencyPrefix}_play_three`,
                `${currencyPrefix}_play_four`,
            ]
                .map(key => {
                    const translated = t(key as CurrencyTranslationKey);
                    // Only include if translation exists (i.e., doesn't return the key itself)
                    // Therefore if _play_two is undefined, then it will not render
                    return translated !== key ? translated : null;
                })
                .filter((item): item is string => item !== null),
        }),
        [currencyPrefix, currencyConfig.color, t]
    );

    const jurisdictionSettings = useJurisdictionStore(state => state.jurisdictionSettings);
    const termsAndConditionsUrl =
        jurisdictionSettings?.productConfig?.screenLinks?.find(link => link.link_type === 'TermsAndConditions')?.url ||
        FALLBACK_TERMS_URL;

    const openUrl = () => {
        navigation.navigate('ModalWebView', { uri: termsAndConditionsUrl });
    };

    return (
        <Modal id="currency-overview" modalRef={modalRef}>
            <>
                <Box mx="s16" mt="s12" mb={isTouchpoint ? 's12' : 's28'}>
                    {!isTouchpoint ? (
                        <Text variant="titleMedium2" textAlign="center" mb="s20" color={ds.colorToken.text.primary}>
                            {t('currency_overview')}
                        </Text>
                    ) : null}

                    {!isTouchpoint ? (
                        <Row gap="s8" backgroundColor={ds.colorToken.background.neutral.base} borderRadius="r96" p="s4">
                            {TAB_CONFIGS.map(tabConfig => {
                                const config = getCurrencyConfig(tabConfig.currency);
                                const Icon = config.icon;
                                return (
                                    <CurrencyTab
                                        key={tabConfig.currency}
                                        isActive={selectedTab === tabConfig.currency}
                                        icon={
                                            <Box style={{ transform: tabConfig.iconTransform }}>
                                                <Icon size={tabConfig.iconSize} />
                                            </Box>
                                        }
                                        label={t(config.labelKey)}
                                        onPress={() => setSelectedTab(tabConfig.currency)}
                                        color={config.color}
                                        iconPosition={tabConfig.iconPosition}
                                    />
                                );
                            })}
                        </Row>
                    ) : null}

                    {isTouchpoint ? (
                        <Box alignItems="center" mt="s20">
                            <Box mb="s18">{React.createElement(currencyConfig.icon, { size: 80 })}</Box>
                            <Text
                                variant="headlineMedium"
                                textAlign="center"
                                mb="s18"
                                color={ds.colorToken.text.primary}
                            >
                                {t(currencyConfig.labelKey)}
                            </Text>
                        </Box>
                    ) : null}
                </Box>

                <Box mx="s16" mb="s26">
                    <Text variant="titleMedium" mb="s12" color={ds.colorToken.text.primary}>
                        {content.title}
                    </Text>

                    {content.getItems.map((text, index) => (
                        <BulletPoint key={`get-${index}`} text={text} color={content.color} />
                    ))}

                    <Text variant="titleMedium" mb="s12" mt="s12" color={ds.colorToken.text.primary}>
                        {content.playTitle}
                    </Text>

                    {content.playItems.map((text, index) => (
                        <BulletPoint key={`play-${index}`} text={text} color={content.color} />
                    ))}
                </Box>
                <Box mx="s16" mb="s16" mt="s2" gap="s12">
                    <Button
                        hierarchy="primary"
                        label={t('common:got_it')}
                        onPress={() => modalRef.current?.dismiss()}
                    />
                    {shouldShowTermsButton(isTouchpoint, isCash) && (
                        <Button
                            label={t('view_terms')}
                            hierarchy="secondary"
                            onPress={() => {
                                modalRef.current?.dismiss();
                                openUrl();
                            }}
                        />
                    )}
                </Box>
            </>
        </Modal>
    );
});
